'use client'

import {
  ArrowUpRightIcon,
  CaretLeftIcon,
  CaretRightIcon,
  Icon,
  IdentificationCardIcon,
  MotorcycleIcon,
  RoadHorizonIcon,
  RocketLaunchIcon,
  TrophyIcon,
} from '@phosphor-icons/react'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { PageInfo } from '@/src/@types/models'
import { FlchFeatureItem, FlchFeaturesBlock } from '@/src/@types/statamic'
import Button from '@/src/components/core/Button'
import Container from '@/src/components/layout/Container'

const ICON_MAP: Record<string, Icon> = {
  trophy: TrophyIcon,
  road_horizon: RoadHorizonIcon,
  identification_card: IdentificationCardIcon,
  rocket_launch: RocketLaunchIcon,
  motorcycle: MotorcycleIcon,
}

export default function Component({
  title,
  text,
  items,
  pageInfo,
}: FlchFeaturesBlock & { pageInfo: PageInfo }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    breakpoints: {
      '(min-width: 768px)': { skipSnaps: true },
    },
  })
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on('select', onSelect).on('reInit', onSelect)
  }, [emblaApi, onSelect])

  if (!items?.length) return null

  return (
    <section
      className={clsx('bg-linear-to-b from-gray-50 to-gray-100 py-24', {
        'mt-20 md:mt-32': pageInfo.newSpacing,
      })}
    >
      <Container>
        <div className="flex max-w-3xl flex-col gap-4 md:gap-6">
          <h2 className="text-4xl font-black text-balance md:text-20xl">
            {title}
          </h2>
          <p className="text-gray-800 md:text-lg">{text}</p>
        </div>
      </Container>

      <div ref={emblaRef} className="mt-8 overflow-hidden md:mt-10">
        <div className="flex items-stretch select-none">
          {items.map((item, i) => (
            <div
              key={i}
              className="ml-5 min-w-0 flex-[0_0_calc(100%-52px)] last:mr-5 min-[1342px]:first:ml-[calc((100%-1280px)/2)] min-[1342px]:last:mr-[calc((100%-1280px)/2)]! md:ml-6 md:flex-[0_0_304px] md:last:mr-6"
            >
              <FeatureCard item={item} />
            </div>
          ))}
        </div>
      </div>
      <Container>
        <div className="mt-6 flex justify-end gap-3">
          <Button
            icon={<CaretLeftIcon weight="bold" />}
            size="icon-small"
            version="gray-outlined"
            onClick={() => emblaApi?.scrollPrev()}
            disabled={!canScrollPrev}
            aria-label="Vorheriges Feature"
          />
          <Button
            icon={<CaretRightIcon weight="bold" />}
            size="icon-small"
            version="gray-outlined"
            onClick={() => emblaApi?.scrollNext()}
            disabled={!canScrollNext}
            aria-label="Nächstes Feature"
          />
        </div>
      </Container>
    </section>
  )
}

function FeatureCard({ item }: { item: FlchFeatureItem }) {
  const IconComponent = ICON_MAP[item.icon?.value]

  return (
    <Link
      href={item.link?.url ?? '#'}
      className="group relative flex h-full flex-col gap-4 rounded-xl border border-gray-200 bg-white p-5 transition-colors hover:border-blue-700 hover:bg-blue-700"
    >
      <span
        className="absolute top-3 right-3 flex size-9 items-center justify-center rounded-full bg-gray-150 text-gray-800 transition-colors group-hover:bg-white group-hover:text-blue-700"
        aria-hidden="true"
      >
        <ArrowUpRightIcon size={20} weight="bold" />
      </span>
      {IconComponent && (
        <IconComponent
          size={32}
          weight="bold"
          className="mt-20 text-blue-700 transition-colors group-hover:text-white"
        />
      )}
      <div className="flex flex-col gap-2">
        <p className="font-black transition-colors group-hover:text-white">
          {item.title}
        </p>
        <p className="text-sm text-gray-800 transition-colors group-hover:text-white">
          {item.text}
        </p>
      </div>
    </Link>
  )
}
